import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { useCategoryOptions } from './CategoryOptionsContext';

const useStyles = makeStyles({
  root: {
    width: '300px',
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiAutocomplete-popupIndicator': {
      width: '36px',
      minWidth: '36px',
      marginTop: 0,
      padding: 0,
      justifyContent: 'center',
    },
    '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
      marginRight: 0,
    },
  },
});

function CategorySelect({ categoryId, onChange, disabled }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const categories = useCategoryOptions();
  const categoryOptions = [
    { id: null, name: t('task.taskEdition.noCategory') },
    ...categories,
  ];
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const option = categories.find((category) => (
      `${category.id}` === `${categoryId}`
    )) || { id: null, name: t('task.taskEdition.noCategory') };
    setSelectedOption(option);
    setInputValue(option.name);
  }, [categoryId, categories, t]);

  const selectCategory = (option) => {
    const selected = option || categoryOptions[0];
    setSelectedOption(selected);
    setInputValue(selected.name);
    onChange(selected.id);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    const query = inputValue.trim().toLowerCase();
    const option = categoryOptions.find((category) => (
      category.name.toLowerCase().includes(query)
    ));
    if (!option) return;

    event.preventDefault();
    // Material UI checks this flag before applying its default Enter behavior.
    // eslint-disable-next-line no-param-reassign
    event.defaultMuiPrevented = true;
    selectCategory(option);
  };

  return (
    <Autocomplete
      options={categoryOptions}
      value={selectedOption}
      inputValue={inputValue}
      onChange={(event, option) => selectCategory(option)}
      onInputChange={(event, value) => setInputValue(value)}
      onKeyDown={handleKeyDown}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, value) => option.id === value.id}
      disableClearable
      disabled={disabled}
      className={styles.root}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('task.taskEdition.category')}
          variant="outlined"
          size="small"
          margin="dense"
        />
      )}
    />
  );
}

CategorySelect.defaultProps = {
  categoryId: null,
  disabled: false,
};

CategorySelect.propTypes = {
  categoryId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CategorySelect;
